import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import FlexTable from "../../../components/ui-components/FlexTable";
import { fmtEur, fmtDate } from "./format";

function capitalize(s) {
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function fmtDays(n) {
  return n == null ? "—" : `${n} days`;
}

const columns = [
  { Header: "Action", accessor: "title" },
  { Header: "Budget", accessor: "budgetName", Cell: ({ value }) => value || "—" },
  { Header: "Owner", accessor: "owner", Cell: ({ value }) => value || "—" },
  { Header: "Target date", accessor: "targetDate", Cell: ({ value }) => fmtDate(value) },
  { Header: "Status", accessor: "status", Cell: ({ value }) => capitalize(value) },
  {
    Header: "Expected saving",
    accessor: "expectedSavingCents",
    align: "right",
    Cell: ({ value }) => (value == null ? "—" : fmtEur(value / 100)),
  },
  {
    Header: "Breach deferred by",
    accessor: "expectedBreachDeferDays",
    align: "right",
    Cell: ({ value }) => fmtDays(value),
  },
];

/**
 * PreventiveActionsBlock - the Preventive Actions page's sole data block (preventive_actions
 * block type). Per the Step-1 decision, this block renders the addressed-exposure badge
 * itself from the same figure's `summary` rather than a separate shared kpi_strip block
 * (whose manifest entry was dropped for this page) - StatusBadgeBlock/KPIStripBlock stay
 * untouched since monthly_summary still depends on them.
 *
 * GET /api/v1/board-reports/{boardReportId}/runs/{runId}/figures/preventive-actions ->
 * envelope key "preventive_actions" -> { actions: [PreventiveAction], summary:
 * { addressedCents, exposureCents, inProgress, total } } (backend internal/boardfigures/
 * budget_risk.go).
 */
const PreventiveActionsBlock = ({ title = "Preventive actions", boardReportId, runId }) => {
  const { data, isLoading, error } = useBlockData(
    `/api/v1/board-reports/${boardReportId}/runs/${runId}/figures/preventive-actions`
  );

  const actions = data?.preventive_actions?.actions ?? [];
  const summary = data?.preventive_actions?.summary ?? null;

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error} className="a4-preventive-actions">
      {summary && (
        <div className="a4-actions-summary" style={{ marginBottom: "4mm" }}>
          <span
            className="a4-actions-summary-badge"
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "1mm 3mm",
              borderRadius: "3mm",
              background: "#eaf3ee",
              color: "#1f7a4d",
              fontSize: "9pt",
              fontWeight: 600,
            }}
          >
            {fmtEur(summary.addressedCents / 100)} of {fmtEur(summary.exposureCents / 100)} exposure addressed
            {" "}({summary.inProgress} in progress of {summary.total})
          </span>
        </div>
      )}

      <FlexTable columns={columns} data={actions} getRowId={(row) => row.id} />
    </BaseBlock>
  );
};

PreventiveActionsBlock.propTypes = {
  title: PropTypes.string,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default PreventiveActionsBlock;
